import { AiAnswerBlock } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AiAnswerBlock'
import AnswerSourcesPanel from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AnswerSourcesPanel'
import OldScrapedAnswerCitations from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/OldScrapedAnswerCitations'
import AnswerValue from '@app/src/pages/Questionnaire/Provider/Report/AnswerValue'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import {
  AnswerStatus,
  ExternalVerificationStatus,
  HistoryStatus,
  ResponseDraftStatus,
} from '@app/src/types/resourceExplorer'
import { Box, Stack } from '@mui/material'
import React from 'react'
import { useReport } from '../../ReportContext'
import VerificationStatus from '../../VerificationStatus'
import SkippedQuestionCard from './SkippedQuestionCard'

export interface AnswerDisplayProps {
  item: ResponseItem
  questionType: string
  unitSymbol: string | null
}

const AnswerDisplay = ({ item, questionType, unitSymbol }: AnswerDisplayProps): JSX.Element => {
  const { reportStatus, request } = useReport()

  const isSomeExternalStatusCorrectionNeeded = item.verifications?.some(
    x => x.externalVerificationStatus === ExternalVerificationStatus.CorrectionNeeded,
  )

  const displayValue = (): React.ReactNode => {
    if (item?.answerStatus !== AnswerStatus.Answered) {
      return (
        <SkippedQuestionCard
          answerStatus={item?.answerStatus}
          estimatedAnswerAt={item?.estimatedAnswerAt}
          isScraped={item?.isScraped}
          comment={item?.comment}
        />
      )
    }

    return <AnswerValue item={item} questionType={questionType} unitSymbol={unitSymbol} />
  }

  if (reportStatus === ResponseDraftStatus.Scraped && item?.answerStatus === AnswerStatus.Answered) {
    return (
      <>
        <AiAnswerBlock>{displayValue()}</AiAnswerBlock>
        <Box px={2.5}>
          <VerificationStatus {...item} />
          {item.scrapedAnswer?.citations?.length ? (
            <OldScrapedAnswerCitations citations={item.scrapedAnswer.citations} provider={request?.targetObject} />
          ) : null}
        </Box>
        {item.isAnswered && item.scrapedAnswer?.searchResults && (
          <AnswerSourcesPanel scrapedAnswer={item.scrapedAnswer} />
        )}
      </>
    )
  }

  return (
    <>
      <Stack px={4} pb={4} pt={2}>
        <Box
          data-pdf-white
          display="flex"
          alignItems="center"
          px={isSomeExternalStatusCorrectionNeeded ? 2 : 0}
          py={isSomeExternalStatusCorrectionNeeded ? 1 : 0}
          bgcolor={
            isSomeExternalStatusCorrectionNeeded
              ? 'warning.light'
              : reportStatus === HistoryStatus.Submitted || reportStatus === HistoryStatus.Approved
                ? 'grey.200'
                : 'common.white'
          }
          borderRadius={1}
        >
          <Box flexGrow={1}>{displayValue()}</Box>
        </Box>

        <VerificationStatus {...item} />
        {item?.isAnswered && item.scrapedAnswer?.citations?.length ? (
          <OldScrapedAnswerCitations citations={item.scrapedAnswer.citations} provider={request?.targetObject} />
        ) : null}
      </Stack>
      {item?.isAnswered && item.scrapedAnswer?.searchResults && (
        <Box px={3} pb={3}>
          <AnswerSourcesPanel scrapedAnswer={item.scrapedAnswer} />
        </Box>
      )}
    </>
  )
}

export default AnswerDisplay
